import { IError } from '@checkout/errors';
import { RootState } from '@checkout/state';
import { Field, Form, Formik, FormikActions, InjectedFormikProps } from 'formik';
import * as React from 'react';
import { Fragment } from 'react';
import { ChangeEvent } from 'react';
import { InjectedIntlProps, IntlProvider, injectIntl } from 'react-intl';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { FormikErrorFocus, PasswordField } from '..';
import { Button, InputField, MembershipHeader, MessageBox, Section } from '../../presentational';
import messages from './../../../i18n/base-en';
import { logInRequest } from './../../../reducers/checkout';
import * as Styles from './LoginForm.scss';
import getValidationSchema from './LoginFormValidationSchema';

interface IOwnProps {
  isFolded: boolean;
  toggleVisibility: (event: ChangeEvent<any>) => void;
  isFetching: boolean;
}

interface IConnectedProps {
  intlData: IntlProvider.Props;
  errors?: IError;
}

interface IConnectetDispatchProps {
  logInRequest: typeof logInRequest;
}

interface IFormValues {
  login: {
    email: string;
    password: string;
  };
}

type Props = IOwnProps & IConnectedProps & IConnectetDispatchProps & InjectedFormikProps<IOwnProps, IFormValues> & InjectedIntlProps;

class LoginForm extends React.Component<Props, {}> {
  validationSchema = () => {
    return getValidationSchema(this.props.intlData);
  }

  onSubmit = (values: IFormValues, formikBag: FormikActions<IFormValues>) => {
    this.props.logInRequest(values.login);
  }

  componentDidMount() {
    const loginField = document.getElementById('login.email');
    if (loginField) {
      loginField.focus();
    }
  }

  componentDidUpdate() {
    const loginField = document.getElementById('login.email');
    if (loginField) {
      loginField.focus();
    }
  }

  render() {
    const { toggleVisibility, isFolded, errors, intl, isFetching } = this.props;
    const { formatMessage } = intl;

    return (
      <div className={Styles.form}>
        <MembershipHeader title={formatMessage(messages.membership_login_title)} subTitle={formatMessage(messages.membership_login_subtitle)} />
        {!isFolded && (
          <Fragment>
            {errors && errors.uiType === 'inline' && <MessageBox spacingBottom={true} type="error" message={errors.messages[0]} />}
            <Formik<IFormValues>
              initialValues={{
                login: {
                  email: '',
                  password: ''
                }
              }}
              validationSchema={this.validationSchema}
              onSubmit={this.onSubmit}
              render={formik => {
                return (
                  <Form className={Styles.form} autoComplete="on">
                    <Section>
                      <Field
                        id="login.email"
                        name="login.email"
                        component={InputField}
                        placeholder={formatMessage(messages.membership_email_placeholder)}
                        label={formatMessage(messages.membership_email_label)}
                        required={true}
                        type="email"
                        submitOnEnter={true}
                        autoComplete="username email"
                      />
                      <Field
                        id="login.password"
                        name="login.password"
                        component={PasswordField}
                        iconTitle={formatMessage(messages.membership_show_password_tooltip)}
                        placeholder={formatMessage(messages.membership_password_placeholder)}
                        label={formatMessage(messages.membership_password_label)}
                        required={true}
                        type="password"
                        autoComplete="current-password"
                        submitOnEnter={true}
                      />
                    </Section>
                    <Button
                      type="submit"
                      variant="primary"
                      align="center"
                      size="medium"
                      width="long"
                      label={formatMessage(messages.membership_login_button_label)}
                      isLoading={isFetching}
                    />

                    <FormikErrorFocus formikContext={formik} />
                  </Form>
                );
              }}
            />
            <div className={Styles.button}>
              <Button
                onClick={toggleVisibility}
                type="button"
                variant="clear"
                align="center"
                size="medium"
                width="long"
                label={formatMessage(messages.membership_forgotten_password)}
              />
            </div>
          </Fragment>
        )}
      </div>
    );
  }
}

export default compose<React.ComponentClass<IOwnProps>>(
  injectIntl,
  connect<IConnectedProps, IConnectetDispatchProps>(
    (state: RootState) => ({
      intlData: state.checkout.intlData,
      errors: state.checkout.errors,
    }),
    {
      logInRequest
    }
  )
)(LoginForm);
